import React, { FC } from 'react';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Transaction } from '~/types';
import c from '~/utils/constants';
import { AmountField, FxRateField, TextField, TransactionResult } from '~/components';
import theme from '~/theme';
import { isDisplayedEntryCategory } from '~/utils/transaction';

interface Props {
  transactions: Transaction[];
  className?: string;
  onClose: () => void;
}

const SuccessScreen: FC<Props> = (props) => {
  const { transactions, className, onClose } = props;
  const [t] = useTranslation('oat');
  const tt = (key: string): string => t(`transactions:${key}`);

  const senderExchangeRate = transactions?.[0]?.details?.currencyConversion?.sender?.rate;
  const recipientExchangeRate = transactions?.[1]?.details?.currencyConversion?.recipient?.rate;

  const creditAmount = transactions?.[1]?.totalAmount;

  return (
    <div className={className}>
      <TransactionResult transaction={transactions?.[0]} onClose={onClose} />
      <div className="SuccessScreen-Result">
        <ResultCell>
          <Space direction="vertical" size={16}>
            <TextField label={t('From account')}>
              {transactions?.[0]?.sender.accountNumber}
            </TextField>
            <TextField label={t('To account')}>
              {transactions?.[0]?.recipient.accountNumber}
            </TextField>
          </Space>
        </ResultCell>
        <ResultCell>
          <Space direction="vertical" size={16}>
            <TextField label={t('Credit amount')}>
              <AmountField amount={creditAmount} />
            </TextField>
            {senderExchangeRate && (
              <>
                <TextField label={tt('exchangeRate')}>
                  <FxRateField
                    baseCurrency={transactions[0]?.baseAmount!.currency as string}
                    quoteCurrency={transactions[0]?.totalAmount.currency as string}
                    rate={senderExchangeRate}
                  />
                </TextField>
              </>
            )}
            {recipientExchangeRate && (
              <>
                <TextField label={tt('exchangeRate')}>
                  <FxRateField
                    baseCurrency={transactions[1]?.baseAmount!.currency as string}
                    quoteCurrency={transactions[1]?.totalAmount.currency as string}
                    rate={recipientExchangeRate}
                  />
                </TextField>
              </>
            )}
            {transactions?.[0]?.entries
              ?.filter((item) => isDisplayedEntryCategory(item.category))
              ?.map((item, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TextField key={index} label={item.description || tt(item.category)}>
                    <AmountField amount={item.amount} abs />
                  </TextField>
                );
              })}
            <TextField label={tt('totalAmount')}>
              <AmountField
                className="SuccessScreen-TotalAmount"
                amount={transactions?.[0]?.totalAmount}
                abs
              />
            </TextField>
          </Space>
        </ResultCell>
      </div>
    </div>
  );
};

const ResultCell: FC<any> = (props) => {
  const { children } = props;
  return <div className="SuccessScreen-ResultCell">{children}</div>;
};

export default styled(SuccessScreen)`
  .SuccessScreen-Result {
    border-radius: ${c.borderRadiusBase}px;
    overflow: hidden;
    border: 1px solid #f0f0f0;
    .SuccessScreen-ResultCell {
      padding: 16px 20px;
      background-color: #ffffff;
      border-bottom: 1px solid #f0f0f0;
      &:last-child {
        border-bottom: none;
      }
    }
  }
  .SuccessScreen-TotalAmount {
    color: ${theme.primaryColor};
  }
`;
